import React, { useState, useEffect } from 'react';
import hangmanService from '../services/hangmanService';
import './HangmanGame.css';

const HangmanGame = () => {
    const [game, setGame] = useState(null);
    const [games, setGames] = useState(null); // For multiple games
    const [currentGameIndex, setCurrentGameIndex] = useState(0); // Track current game in session
    const [guess, setGuess] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState({ topic: '', level: 'easy', wordCount: 5 });
    const [userGames, setUserGames] = useState([]);
    const [showGamesList, setShowGamesList] = useState(false);
    const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(0);

    const topicSuggestions = [
        'động vật', 'trái cây', 'quốc gia', 'đồ vật', 'khoa học',
        'thể thao', 'phim ảnh', 'đồ ăn', 'nghề nghiệp', 'màu sắc',
        'động vật biển', 'món ăn Việt Nam', 'công nghệ', 'xe cộ',
        'nhạc cụ', 'thời tiết', 'gia đình', 'trường học'
    ];

    const levelDisplayNames = {
        'easy': 'Dễ',
        'medium': 'Trung bình',
        'hard': 'Khó'
    };

    // Auto-advance to next game when current game is won
    useEffect(() => {
        if (game && game.status === 'won' && games && games.games.length > 1 && currentGameIndex < games.games.length - 1) {
            setAutoAdvanceCountdown(3);
            const timer = setInterval(() => {
                setAutoAdvanceCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleNextGame();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        } else {
            setAutoAdvanceCountdown(0);
        }
    }, [game?.status, games, currentGameIndex]);

    // Reset countdown when manually navigating
    useEffect(() => {
        setAutoAdvanceCountdown(0);
    }, [currentGameIndex]);

    const handleStartGame = async () => {
        // Validation
        if (!options.topic || options.topic.trim() === '') {
            setError('Vui lòng nhập chủ đề');
            return;
        }

        if (options.wordCount < 1 || options.wordCount > 50) {
            setError('Số lượng từ phải từ 1 đến 50');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const result = await hangmanService.startGame(options.topic.trim(), options.level, options.wordCount);

            // Always start with the first game
            setGames(result);
            setGame(result.games[0]);
            setCurrentGameIndex(0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectGame = (selectedGame) => {
        setGame(selectedGame);
        setShowGamesList(false); // Hide the games list

        // Find the index of the selected game if games exist
        if (games && games.games) {
            const gameIndex = games.games.findIndex(g => g.gameId === selectedGame.gameId);
            if (gameIndex !== -1) {
                setCurrentGameIndex(gameIndex);
            }
        } else {
            // If coming from user games list, reset games state and index
            setGames(null);
            setCurrentGameIndex(0);
        }
    };

    const handleNextGame = () => {
        if (games && currentGameIndex < games.games.length - 1) {
            const nextIndex = currentGameIndex + 1;
            setCurrentGameIndex(nextIndex);
            setGame(games.games[nextIndex]);
            setAutoAdvanceCountdown(0); // Cancel any ongoing countdown
        }
    };

    const handlePreviousGame = () => {
        if (games && currentGameIndex > 0) {
            const prevIndex = currentGameIndex - 1;
            setCurrentGameIndex(prevIndex);
            setGame(games.games[prevIndex]);
            setAutoAdvanceCountdown(0); // Cancel any ongoing countdown
        }
    };

    const cancelAutoAdvance = () => {
        setAutoAdvanceCountdown(0);
    };

    const loadUserGames = async () => {
        setLoading(true);
        try {
            const result = await hangmanService.getUserGames('in_progress', 20);
            setUserGames(result.games);
            setShowGamesList(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGuessSubmit = async (e) => {
        e.preventDefault();
        if (!guess || guess.length !== 1 || !/^[a-zA-Z]$/.test(guess)) {
            setError('Vui lòng nhập một chữ cái hợp lệ.');
            return;
        }
        if (game && Array.isArray(game.guessedLetters) && game.guessedLetters.includes(guess.toLowerCase())) {
            setError('Bạn đã đoán chữ cái này rồi.');
            setGuess('');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const updatedGame = await hangmanService.makeGuess(game.gameId, guess.toLowerCase());
            setGame(updatedGame);

            // Update the game in the games array if it exists
            if (games && games.games) {
                const updatedGames = { ...games };
                updatedGames.games[currentGameIndex] = updatedGame;
                setGames(updatedGames);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setGuess('');
        }
    };

    const renderWord = () => {
        if (!game) return null;
        // If displayWord is not a string, try to show underscores based on game.letters
        if (typeof game.displayWord !== 'string') {
            if (typeof game.letters === 'number') {
                return Array(game.letters).fill('_').join(' ');
            }
            return ''; // Return empty string if we can't determine the length
        }
        return game.displayWord; // Backend now provides the correctly spaced string
    };

    // With the backend fixed, we can now trust the data.
    const guessedLetters = (game && Array.isArray(game.guessedLetters)) ? game.guessedLetters : [];
// Safely get displayWord, default to empty string if not available
    const displayWord = (game && typeof game.displayWord === 'string') ? game.displayWord : '';
    const incorrectGuesses = guessedLetters.filter(letter => !displayWord.includes(letter));

    return (
        <div className="hangman-container">
            <div className="hangman-content">
                <div style={{width: '100%', maxWidth: '1400px'}}>
                    {error && <p className="error-message">{error}</p>}

            {!game && !showGamesList ? (
                <div className="game-setup">
                    <div className="form-group">
                        <label htmlFor="topic-input">Chủ đề</label>
                        <input
                            id="topic-input"
                            type="text"
                            value={options.topic}
                            onChange={e => setOptions({...options, topic: e.target.value})}
                            placeholder="VD: động vật, trái cây, công nghệ..."
                            className="topic-input"
                        />
                        <div className="topic-suggestions">
                            <span className="suggestions-label">Gợi ý:</span>
                            {topicSuggestions.slice(0, 6).map(suggestion => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    className="suggestion-btn"
                                    onClick={() => setOptions({...options, topic: suggestion})}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <label htmlFor="level-select">Độ khó</label>
                        <select id="level-select" value={options.level} onChange={e => setOptions({...options, level: e.target.value})}>
                            <option value="easy">Dễ</option>
                            <option value="medium">Trung bình</option>
                            <option value="hard">Khó</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="word-count-input">Số lượng từ</label>
                        <input
                            id="word-count-input"
                            type="number"
                            value={options.wordCount}
                            onChange={e => setOptions({...options, wordCount: Math.max(1, Math.min(50, parseInt(e.target.value) || 1))})}
                            min="1"
                            max="50"
                            className="word-count-input"
                        />
                        <div className="input-hint">
                            Từ 1 đến 50 từ
                        </div>
                    </div>

                    <div className="button-group">
                        <button className="play-btn" onClick={handleStartGame} disabled={loading}>
                            {loading ? 'Đang tạo game...' : `Tạo ${options.wordCount} từ`}
                        </button>

                        <button className="secondary-btn" onClick={loadUserGames} disabled={loading}>
                            {loading ? 'Đang tải...' : 'Xem games đã tạo'}
                        </button>
                    </div>
                </div>
            ) : !game && games ? (
                <div className="games-list">
                    <h3>Chọn từ để chơi ({games.totalCount} từ - {games.topic} - {levelDisplayNames[games.level]})</h3>
                    <div className="games-grid">
                        {games.games.map((gameItem, index) => (
                            <div key={gameItem.gameId} className="game-card" onClick={() => handleSelectGame(gameItem)}>
                                <div className="game-number">#{index + 1}</div>
                                <div className="word-preview">{gameItem.displayWord}</div>
                                {gameItem.imageUrl && (
                                    <img src={gameItem.imageUrl} alt={gameItem.imageAlt} className="game-card-image" />
                                )}
                                <div className="game-status">
                                    <span className="attempts">Còn {gameItem.remainingAttempts} lượt</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="back-btn" onClick={() => {setGames(null); setGame(null);}}>
                        Tạo games mới
                    </button>
                </div>
            ) : !game && showGamesList ? (
                <div className="user-games-list">
                    <h3>Games đang chơi của bạn ({userGames.length})</h3>
                    {userGames.length > 0 ? (
                        <div className="games-grid">
                            {userGames.map((gameItem, index) => (
                                <div key={gameItem.id} className="game-card" onClick={() => handleSelectGame(gameItem)}>
                                    <div className="game-info">
                                        <div className="topic-level">{gameItem.topic} - {levelDisplayNames[gameItem.level]}</div>
                                        <div className="word-preview">{gameItem.displayWord}</div>
                                    </div>
                                    {gameItem.imageUrl && (
                                        <img src={gameItem.imageUrl} alt={gameItem.imageAlt} className="game-card-image" />
                                    )}
                                    <div className="game-status">
                                        <span className="attempts">Còn {gameItem.remainingAttempts} lượt</span>
                                        <span className="date">{new Date(gameItem.createdAt).toLocaleDateString('vi-VN')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Bạn chưa có game nào đang chơi.</p>
                    )}
                    <button className="back-btn" onClick={() => setShowGamesList(false)}>
                        Tạo game mới
                    </button>
                </div>
            ) : game ? (
                <>
                    {game.status === 'in_progress' ? (
                        <div className="game-area">
                            <div className="game-info-header">
                                <p className="topic-level-info">Chủ đề: {game.topic} - Cấp độ: {levelDisplayNames[game.level] || game.level}</p>
                                <p className="remaining-attempts">
                                    Số lần đoán còn lại: <strong>{game.remainingAttempts}</strong>
                                </p>
                            </div>
                            
                            <div className="game-area-left">
                                {game.imageUrl ? (
                                    <div className="hangman-image-container">
                                        <img src={game.imageUrl} alt={game.imageAlt || 'Gợi ý'} className="hangman-image" />
                                        {game.imageSource && game.imageSource !== 'placeholder' && (
                                            <p className="image-source-credit">Photo from {game.imageSource}</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="hangman-image-container">Đang tìm hình ảnh...</div>
                                )}
                            </div>
                            
                            <div className="game-area-center">
                                <div className="word-display">{renderWord()}</div>

                                <form onSubmit={handleGuessSubmit} className="guess-form">
                                    <input
                                        type="text"
                                        value={guess}
                                        onChange={(e) => setGuess(e.target.value.toLowerCase())}
                                        maxLength="1"
                                        required
                                        autoFocus
                                        disabled={loading || game.status !== 'in_progress'}
                                        placeholder="Nhập"
                                    />
                                    <button type="submit" disabled={loading || game.status !== 'in_progress'}>Đoán</button>
                                </form>

                                <div className="guesses-container">
                                    <p className="incorrect-guesses">
                                        <span>Đã đoán sai: </span>
                                        {incorrectGuesses.length > 0 ? incorrectGuesses.join(', ') : 'Chưa có'}
                                    </p>
                                </div>
                            
                                <div className="game-actions">
                                {games && games.games.length > 1 && (
                                    <div className="navigation-buttons">
                                        <button
                                            onClick={handlePreviousGame}
                                            className="nav-btn"
                                            disabled={currentGameIndex === 0}
                                        >
                                            ← Từ trước
                                        </button>
                                        <span className="game-counter">
                                            {currentGameIndex + 1} / {games.games.length}
                                        </span>
                                        <button
                                            onClick={handleNextGame}
                                            className="nav-btn"
                                            disabled={currentGameIndex === games.games.length - 1}
                                        >
                                            Từ tiếp →
                                        </button>
                                    </div>
                                )}

                                <div className="action-buttons">
                                    <button onClick={() => {setGame(null); setGames(null); setShowGamesList(false); setCurrentGameIndex(0);}} className="new-game-btn">
                                        Tạo game mới
                                    </button>
                                </div>
                            </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`game-over-message ${game.status === 'won' ? 'status-won' : 'status-lost'}`}>
                            <h3>{game.status === 'won' ? '🎉 Chúc mừng, bạn đã thắng! 🎉' : '😢 Rất tiếc, bạn đã thua! 😢'}</h3>

                            {autoAdvanceCountdown > 0 && (
                                <div className="auto-advance-notice">
                                    <p>Tự động chuyển sang từ tiếp theo sau <strong>{autoAdvanceCountdown}</strong> giây...</p>
                                    <button onClick={cancelAutoAdvance} className="cancel-auto-btn">
                                        Hủy tự động chuyển
                                    </button>
                                </div>
                            )}
                            {game.imageUrl && (
                                <div className="hangman-image-container">
                                    <img src={game.imageUrl} alt={game.imageAlt || `Đáp án: ${game.word}`} className="hangman-image" />
                                    {game.imageSource && game.imageSource !== 'placeholder' && (
                                        <p className="image-source-credit">Photo from {game.imageSource}</p>
                                    )}
                                </div>
                            )}
                            <p>Từ đúng là: <strong>{game.word}</strong></p>
                            <div className="game-over-actions">
                                {games && games.games.length > 1 && (
                                    <div className="navigation-buttons">
                                        <button
                                            onClick={handlePreviousGame}
                                            className="nav-btn"
                                            disabled={currentGameIndex === 0}
                                        >
                                            ← Từ trước
                                        </button>
                                        <span className="game-counter">
                                            {currentGameIndex + 1} / {games.games.length}
                                        </span>
                                        <button
                                            onClick={handleNextGame}
                                            className="nav-btn"
                                            disabled={currentGameIndex === games.games.length - 1}
                                        >
                                            Từ tiếp →
                                        </button>
                                    </div>
                                )}

                                <div className="action-buttons">
                                    <button onClick={() => {setGame(null); setGames(null); setShowGamesList(false); setCurrentGameIndex(0);}} className="play-again-button">
                                        Chơi lại
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : null}
                </div>
            </div>
        </div>
    );
};

export default HangmanGame;
